import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import MetRate from '../../components/Plots/MetRate/MetRate';
import HeartRate from '../../components/Plots/HeartRate/HeartRate';
import EcsSuit from '../../components/Plots/EcsSuit/EcsSuit';
import Thermal from '../../components/Plots/Thermal/Thermal';
import { useDashboardPreferences } from '../../contexts/DashboardPreferencesContext';

const CARD_DATA_MAP = {
  CHAPEA: {
    metRate: {
      dataKey: 'chapeaData', minKey: 'chapeaMinBtuHR', maxKey: 'chapeaMaxBtuHR', pressureKey: 'chapeaSuitPressure',
    },
    heartRate: { dataKey: 'chapeaData', minKey: 'chapeaMinHrBPM', maxKey: 'chapeaMaxHrBPM' },
    ecsSuit: null,
    thermal: null,
  },
  NBL: {
    metRate: {
      dataKey: 'nblData', minKey: 'nblMinBtuHR', maxKey: 'nblMaxBtuHR', pressureKey: 'nblSuitPressure',
    },
    heartRate: { dataKey: 'nblData', minKey: 'nblMinHrBPM', maxKey: 'nblMaxHrBPM' },
    ecsSuit: { dataKey: 'nblECSData' },
    thermal: null,
  },
  ARGOS: {
    metRate: {
      dataKey: 'argosData', minKey: 'argosMinBtuHR', maxKey: 'argosMaxBtuHR', pressureKey: 'argosSuitPressure',
    },
    heartRate: { dataKey: 'argosData', minKey: 'argosMinHrBPM', maxKey: 'argosMaxHrBPM' },
    ecsSuit: null,
    thermal: { dataKey: 'thermalData', source: 'thermal' },
  },
  'FIELD TEST': {
    metRate: {
      dataKey: 'fieldTestData', minKey: 'fieldTestMinBtuHR', maxKey: 'fieldTestMaxBtuHR', pressureKey: 'fieldTestSuitPressure',
    },
    heartRate: { dataKey: 'fieldTestData', minKey: 'fieldTestMinHrBPM', maxKey: 'fieldTestMaxHrBPM' },
    ecsSuit: null,
    thermal: null,
  },
};

const CARD_LABELS = {
  metRate: 'Met Rate',
  heartRate: 'Heart Rate',
  ecsSuit: 'ECS Suit',
  thermal: 'Thermal',
};

const LOCATION_MAP = {
  CHAPEA: 'CHAPEA',
  NBL: 'NBL',
  ARGOS: 'ARGOS',
  'FIELD TEST': 'FIELD TEST',
};

function Dashboard({ webSocketService, thermalWebsocketService }) {
  const {
    environment,
    selectedCards,
    cardOrder,
    setCardOrder,
  } = useDashboardPreferences();

  const [state, setState] = useState(webSocketService?.state);
  const [tState, setTState] = useState(thermalWebsocketService?.state);

  useEffect(() => {
    const wss = webSocketService;
    const tWss = thermalWebsocketService;
    if (wss) {
      wss.setState = setState;
      setState(wss.state);
    }
    if (tWss) {
      tWss.setState = setTState;
      setTState(tWss.state);
    }
    return () => {
      if (wss) wss.setState = () => {};
      if (tWss) tWss.setState = () => {};
    };
  }, [webSocketService, thermalWebsocketService]);

  const orderedCards = cardOrder.filter((id) => selectedCards.includes(id));
  const location = LOCATION_MAP[environment] || 'NBL';

  const handleDragStart = useCallback((e, cardId) => {
    e.dataTransfer.setData('text/plain', cardId);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback((e, targetCardId) => {
    e.preventDefault();
    const sourceCardId = e.dataTransfer.getData('text/plain');
    if (!sourceCardId || sourceCardId === targetCardId) return;
    const newOrder = [...orderedCards];
    const sourceIdx = newOrder.indexOf(sourceCardId);
    const targetIdx = newOrder.indexOf(targetCardId);
    if (sourceIdx === -1 || targetIdx === -1) return;
    newOrder.splice(sourceIdx, 1);
    newOrder.splice(targetIdx, 0, sourceCardId);
    const rest = cardOrder.filter((id) => !newOrder.includes(id));
    setCardOrder([...newOrder, ...rest]);
  }, [orderedCards, cardOrder, setCardOrder]);

  const renderCard = (cardId) => {
    const mapping = CARD_DATA_MAP[environment]?.[cardId];
    if (!mapping) return null;

    if (mapping.source === 'thermal') {
      const data = (tState || {}).data || [];
      return (
        <Thermal
          key={cardId}
          data={data}
          minRTD={tState?.minRTD}
          maxRTD={tState?.maxRTD}
          compact
        />
      );
    }

    const data = (state || {})[mapping.dataKey] || [];
    const minVal = mapping.minKey ? (state || {})[mapping.minKey] : undefined;
    const maxVal = mapping.maxKey ? (state || {})[mapping.maxKey] : undefined;
    const pressureVal = mapping.pressureKey ? (state || {})[mapping.pressureKey] : undefined;

    switch (cardId) {
      case 'metRate':
        return (
          <MetRate
            key={cardId}
            location={location}
            data={data}
            minBtuHR={minVal}
            maxBtuHR={maxVal}
            suitPressure={pressureVal}
            compact
          />
        );
      case 'heartRate':
        return (
          <HeartRate
            key={cardId}
            data={data}
            minHrBPM={minVal}
            maxHrBPM={maxVal}
            compact
          />
        );
      case 'ecsSuit':
        return <EcsSuit key={cardId} data={data} compact />;
      default:
        return null;
    }
  };

  if (orderedCards.length === 0) {
    return (
      <section>
        <div className="Dashboard scene flex-col">
          <p className="dashboard-empty-message">Select at least one biometric card to display.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="dashboard-section">
      <div className="Dashboard scene flex-col">
        <div className="flex-row info dashboard-cards-row">
          {orderedCards.map((cardId) => (
            <div
              key={cardId}
              className="dashboard-card-wrapper"
              draggable
              onDragStart={(e) => handleDragStart(e, cardId)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, cardId)}
            >
              {renderCard(cardId)}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

Dashboard.propTypes = {
  // eslint-disable-next-line react/no-unused-prop-types -- used in useState/useEffect
  webSocketService: PropTypes.any,
  // eslint-disable-next-line react/no-unused-prop-types -- used in useState/useEffect
  thermalWebsocketService: PropTypes.any,
};

export default Dashboard;
export { CARD_LABELS };
